import { useDatabase } from '../composables/database'
import { useRecapStore } from '../store/useRecapStore'
import { RecapResponse } from '../types/recap'
import { RecapImagesModel } from './RecapImagesModel'
import { storeToRefs } from 'pinia'
import { logInfo } from '../utils'
import { Ref, ref } from 'vue'

type RecapResponsesModelReturn = {
  isSavingRecap: Ref<boolean>
  getAllRecapResponses: () => Promise<RecapResponse[]>
  getAllCompleteRecapResponses: () => Promise<RecapResponse[]>
  getResponseForTask: (task_id: string) => Promise<RecapResponse | null>
  getRecapResponseExistsForTask: (task_id: string) => Promise<boolean>
  writeRecapResponses: (responses: RecapResponse[]) => Promise<boolean>
  removeStaleRecapResponses: () => Promise<void>
  removeByTaskId: (task_id: string) => Promise<void>
  clearRecapResponses: () => Promise<void>
  flagResponseComplete: (task_id: string) => Promise<void>
  flagResponseIncomplete: (task_id: string) => Promise<void>
}

export type DatabaseRecapResponse = {
  task_id: string
  recap_id: string
  response: string
  date_uploaded: string
  is_complete: boolean
}

/**
 * Interacts with the recap respponses table
 * @returns {RecapResponsesModelReturn}
 */
const isSavingRecap = ref(false)
export const RecapResponsesModel = (): RecapResponsesModelReturn => {
  const { run, query } = useDatabase()
  const recapStore = storeToRefs(useRecapStore())

  async function getAllRecapResponses(): Promise<RecapResponse[]> {
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_responses
      `
      const recapsResponse = ((await query(sql)) as unknown as DatabaseRecapResponse[]) || []
      return recapsResponse.map((response) => JSON.parse(response.response))
    } catch (error) {
      logInfo('error', 'getAllRecapResponses', 'Error fetching all recap responses', { error })
      return []
    }
  }

  async function getAllCompleteRecapResponses(): Promise<RecapResponse[]> {
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_responses
        WHERE
          is_complete IS TRUE
      `
      const recapsResponse = ((await query(sql)) as unknown as DatabaseRecapResponse[]) || []
      return recapsResponse.map((response) => JSON.parse(response.response))
    } catch (error) {
      logInfo(
        'error',
        'getAllCompleteRecapResponses',
        'Error fetching all complete recap responses',
        { error }
      )
      return []
    }
  }

  async function getResponseForTask(task_id: string): Promise<RecapResponse | null> {
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_responses
        WHERE
          task_id = ${task_id}
      `
      const recapResponse = ((await query(sql)) as unknown as DatabaseRecapResponse[]) || []
      if (recapResponse.length) {
        const response = recapResponse[0]
        return JSON.parse(response.response)
      }
    } catch (error) {
      logInfo('error', 'getResponseForTask', 'Error fetching recap response for task', {
        error,
        task_id,
      })
    }
    return null
  }

  async function getRecapResponseExistsForTask(task_id: string): Promise<boolean> {
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_responses
        WHERE
          task_id = ${task_id}
        LIMIT 1
      `
      const recapResponse = ((await query(sql)) as unknown as DatabaseRecapResponse[]) || []
      return !!recapResponse.length
    } catch (error) {
      logInfo('error', 'getRecapResponseExistsForTask', 'Error fetching recap response for task', {
        error,
        task_id,
      })
      return false
    }
  }

  async function writeRecapResponses(responses: RecapResponse[]): Promise<boolean> {
    const failedResponses: RecapResponse[] = []
    for (const response of responses) {
      try {
        const sql = `
          INSERT OR REPLACE INTO recap_responses(task_id, recap_id, response, date_uploaded, is_complete)
          VALUES (?, ?, ?, date(), ?)
        `
        await run(sql, [
          response.task_id,
          response.events_recaps_id,
          JSON.stringify(response),
          false,
        ])
      } catch (error) {
        logInfo('error', 'writeRecapResponses', 'Error writing recap response', { error, response })
        failedResponses.push(response)
      }
    }
    return !(failedResponses && failedResponses.length)
  }

  // Remove any images over 14 days old
  async function removeStaleRecapResponses() {
    try {
      const sql = `
        DELETE FROM recap_responses WHERE date_uploaded <= date('now','-14 day')
      `
      await run(sql, [])
    } catch (error) {
      logInfo('error', 'removeStaleRecapResponses', 'Error deleting stale recap responses', {
        error,
      })
    }
  }

  async function removeByTaskId(task_id: string) {
    try {
      const sql = `
        DELETE FROM recap_responses WHERE task_id = ?
      `
      // Delete the images for the response, and then the responses
      await RecapImagesModel().removeByTaskId(task_id)
      await run(sql, [task_id])

      const indexes = recapStore.recap_responses.value
        .map((response, index) => (response.task_id === task_id ? index : null))
        .filter((index) => index !== null)
        .reverse() // We need to reverse this so we can remove each response by index (starting from the last item)

      // Splice the responses from the array of all responses
      for (const idx of indexes) {
        if (idx !== null) {
          recapStore.recap_responses.value.splice(+idx, 1)
        }
      }
    } catch (error) {
      logInfo('error', 'removeByTaskId', 'Error removing recap responses by task ID', {
        error,
        task_id,
      })
      throw error
    }
  }

  async function clearRecapResponses() {
    try {
      const sql = `
        DELETE FROM recap_responses
      `
      await run(sql, [])
      recapStore.recap_responses.value.splice(0, recapStore.recap_responses.value.length)
    } catch (error) {
      logInfo('error', 'clearRecapResponses', 'Error clearing recap responses', { error })
    }
  }

  async function flagResponseComplete(task_id: string) {
    try {
      const sql = `
        UPDATE
          recap_responses
        SET
          is_complete = TRUE
        WHERE
          task_id = ?
      `
      await run(sql, [task_id])
    } catch (error) {
      logInfo('error', 'flagResponseComplete', 'Error flagging response as complete', { error })
    }
  }

  async function flagResponseIncomplete(task_id: string) {
    try {
      const sql = `
        UPDATE
          recap_responses
        SET
          is_complete = FALSE
        WHERE
          task_id = ?
      `
      await run(sql, [task_id])
    } catch (error) {
      logInfo('error', 'flagResponseIncomplete', 'Error flagging response as incomplete', { error })
    }
  }

  return {
    isSavingRecap,
    getAllRecapResponses,
    getAllCompleteRecapResponses,
    getResponseForTask,
    getRecapResponseExistsForTask,
    writeRecapResponses,
    removeStaleRecapResponses,
    removeByTaskId,
    clearRecapResponses,
    flagResponseComplete,
    flagResponseIncomplete,
  }
}
